// The per-line state behind "a queued per-copy change shows at once"
// (useCopyMutations.queuePatch): the confirmed baseline of a copy line and
// the PATCHes queued on it. Owner data, so it is bound to the owner and the
// session it was made in (Codex r2 #6):
//  - keyed by (owner, collection, copy line);
//  - each state carries the store EPOCH, the offline owner INCARNATION and
//    the write-fence capture it was created under (Codex r2 #5); a state
//    that no longer matches all three is dead: nothing may show, replay or
//    settle it;
//  - clearCopyLineEdits() empties the store and bumps the epoch. It runs on
//    the real owner purge (offline appAdapter's C3 wipe) and on every owner
//    change the capability store sees (sign-out, another account), so a
//    late callback of a departed owner finds its state dead.
import { currentWriterCtx } from './offline/offlineContext'

export interface LineEditsState<Item, Edit> {
  readonly owner: string
  readonly collectionId: string
  readonly epoch: number
  readonly incarnation: number | null
  /** The write-fence capture at creation (collectionWriteFence.captureCopiesWriteContext). */
  readonly fenceLive: () => boolean
  base: Item
  edits: Edit[]
  touched: Set<string>
}

const states = new Map<string, LineEditsState<unknown, unknown>>()
let epoch = 0

/** The offline owner incarnation this tab writes under (null without the runtime). */
export function currentIncarnation(): number | null {
  return currentWriterCtx()?.incarnation ?? null
}

export function lineEditsKey(owner: string, collectionId: string, copyId: number): string {
  // eslint-disable-next-line i18next/no-literal-string -- a map key, never shown
  return `${owner}\u0000${collectionId}#${copyId}`
}

export function lineEditsEpoch(): number {
  return epoch
}

export function getLineEdits<Item, Edit>(key: string): LineEditsState<Item, Edit> | undefined {
  return states.get(key) as LineEditsState<Item, Edit> | undefined
}

export function setLineEdits<Item, Edit>(key: string, state: LineEditsState<Item, Edit>): void {
  states.set(key, state as LineEditsState<unknown, unknown>)
}

/** Drop `state` (only if it is still the one stored under `key`). */
export function deleteLineEdits(key: string, state: LineEditsState<unknown, unknown> | LineEditsState<never, never>): void {
  if (states.get(key) === state) states.delete(key)
}

/**
 * The state is still the live one: stored under `key`, made in this epoch
 * and this owner incarnation, and its write-fence capture still holds. A
 * dead state is dropped here.
 */
export function lineEditsLive(key: string, state: LineEditsState<unknown, unknown> | LineEditsState<never, never>): boolean {
  const live = states.get(key) === state
    && state.epoch === epoch
    && state.incarnation === currentIncarnation()
    && state.fenceLive()
  if (!live) deleteLineEdits(key, state)
  return live
}

/** Forget every line's state (owner purge, owner change); outstanding callbacks find theirs dead. */
export function clearCopyLineEdits(): void {
  epoch += 1
  states.clear()
}

/** Test seam. */
export function __lineEditsSize(): number {
  return states.size
}
